import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface TallyFormSummary {
  id: string;
  name: string;
  workspaceId?: string;
  status?: string;
  numberOfSubmissions?: number;
}

export interface TallyFormField {
  key: string;
  label: string;
  type: string;
  options?: Array<{ id: string; text: string }>;
}

export interface TallyMappingRule {
  target: string;
  label?: string;
  crm_use?: boolean;
  analytics_use?: boolean;
  ai_profile_use?: boolean;
  sensitive?: boolean;
  workflow_key?: string;
}

export interface TallyConnectionState {
  connections: Array<Record<string, any>>;
  forms: Array<Record<string, any>>;
  failures: Array<Record<string, any>>;
}

async function invokeTally(
  businessId: string,
  action: string,
  input: Record<string, unknown> = {},
) {
  const { data, error } = await supabase.functions.invoke("tally-connector", {
    body: { action, business_id: businessId, ...input },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

export function useTallyConnection(businessId?: string | null) {
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => ["tally-connection", businessId] as const,
    [businessId],
  );
  const status = useQuery({
    queryKey,
    queryFn: () =>
      invokeTally(businessId!, "status") as Promise<TallyConnectionState>,
    enabled: !!businessId,
    refetchInterval: (query) => {
      const state = query.state.data as TallyConnectionState | undefined;
      const busy = state?.forms?.some((form) =>
        ["queued", "running"].includes(String(form.backfill_state)),
      );
      return busy ? 5_000 : false;
    },
  });

  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey }),
    [queryClient, queryKey],
  );

  const mutation = useMutation({
    mutationFn: async ({
      action,
      input,
    }: {
      action: string;
      input?: Record<string, unknown>;
    }) => invokeTally(businessId!, action, input),
    onSuccess: refresh,
  });

  return {
    ...status,
    state: status.data,
    busy: mutation.isPending,
    mutateAsync: mutation.mutateAsync,
    refresh,
  };
}
